import React, { useEffect, useState } from "react";
import { Megaphone } from "lucide-react";
import DashboardCard from "../components/DashboardCard";
import DashboardCardSkeleton from "../components/DashboardCardSkeleton";
import DashboardPagination from "../components/DashboardPagination";
import { DASHBOARD_LIST_PAGE_SIZE } from "../constants/dashboardListPagination";
import { AvailableOwnerListing, OwnerPatrimonyRequestStatus } from "../types/ownerDashboard.types";
import { paginateItemsLocally } from "../utils/paginateItemsLocally";
import { AVAILABLE_LISTINGS_SECTION_ID } from "../utils/selectAvailableOwnerListings";
import AvailablePropertyCard from "./AvailablePropertyCard";

interface AvailablePropertiesProps {
  listings: AvailableOwnerListing[];
  status: OwnerPatrimonyRequestStatus;
  smDown: boolean;
  lgDown?: boolean;
}

function getCardPadding(smDown: boolean, lgDown: boolean) {
  if (smDown) return "0.875rem";
  if (lgDown) return "1rem";
  return "1.125rem";
}

export default function AvailableProperties({
  listings,
  status,
  smDown,
  lgDown = false,
}: AvailablePropertiesProps) {
  const [page, setPage] = useState(1);
  const { items: visibleListings, pagination } = paginateItemsLocally(
    listings,
    page,
    DASHBOARD_LIST_PAGE_SIZE
  );

  useEffect(() => {
    setPage(1);
  }, [listings.length]);

  useEffect(() => {
    if (page > pagination.last_page) {
      setPage(pagination.last_page);
    }
  }, [page, pagination.last_page]);

  if (status === "idle") {
    return null;
  }

  if (status === "loading") {
    return <DashboardCardSkeleton minHeight="min-h-[10rem]" lines={3} />;
  }

  if (status === "error" || listings.length === 0) {
    return null;
  }

  const padding = getCardPadding(smDown, lgDown);

  return (
    <DashboardCard padding={padding} id={AVAILABLE_LISTINGS_SECTION_ID} className="scroll-mt-24">
      <div className="mb-4 flex items-start gap-3">
        <Megaphone className="mt-0.5 h-5 w-5 shrink-0 text-[#6ea8ff]" aria-hidden />
        <div className="min-w-0">
          <h2 className="m-0 text-[clamp(1rem,1.4vw,1.25rem)] font-semibold text-[#f0f6fc]">
            Imóveis anunciados
          </h2>
          <p className="mt-1 text-xs text-[#8b949e] sm:text-sm">
            Abra o anúncio público para ver fotos, descrição e características de cada imóvel
          </p>
        </div>
      </div>

      <div className="flex flex-col gap-3" data-testid="available-properties-list">
        {visibleListings.map((listing) => (
          <AvailablePropertyCard key={listing.id} listing={listing} />
        ))}
      </div>

      <DashboardPagination
        currentPage={pagination.current_page}
        totalPages={pagination.last_page}
        onPageChange={setPage}
      />
    </DashboardCard>
  );
}
